import React, { useEffect, useState } from 'react';
import { Box, Stack, FormControl, FormLabel, Input, Button, Alert, Table, Sheet, CircularProgress } from '@mui/joy';
import { useAdmin } from '../../contexts/Admin.tsx';
import type { AdminToken } from '../../api/admin.ts';
import { PageHeader, SectionCard } from '../../components/admin/Page.tsx';

export default function AdminTokens() {
  const { api } = useAdmin();
  const [tokens, setTokens] = useState<AdminToken[]>([]);
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  const reload = async () => {
    try {
      const list = await api.listAdminTokens();
      setTokens(list);
    } catch (err: any) {
      setError(err.message);
    }
  };

  useEffect(() => {
    reload().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setCreating(true);
    setError('');
    try {
      await api.createAdminToken(name.trim());
      setName('');
      await reload();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this token?')) return;
    try {
      await api.deleteAdminToken(id);
      await reload();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const copy = (text: string) => navigator.clipboard.writeText(text);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
      <PageHeader title="API Tokens" subtitle="Long-lived tokens for programmatic access" />
      {error && <Alert color="danger" variant="soft" sx={{ mb: 2 }}>{error}</Alert>}

      <Stack spacing={3}>
        <SectionCard title="Create token">
          <form onSubmit={handleCreate}>
            <Stack direction="row" spacing={1} alignItems="flex-end">
              <FormControl sx={{ flex: 1 }}>
                <FormLabel>Token name</FormLabel>
                <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. mobile-app" />
              </FormControl>
              <Button type="submit" loading={creating} disabled={!name.trim()}>Generate</Button>
            </Stack>
          </form>
        </SectionCard>

        <Sheet variant="outlined" sx={{ borderRadius: 'lg', overflow: 'auto', boxShadow: 'sm' }}>
          <Table hoverRow sx={{ '& thead th': { bgcolor: 'background.level1', fontWeight: 600 } }}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Token</th>
                <th>Created</th>
                <th style={{ width: 180 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {tokens.length === 0 && (
                <tr><td colSpan={4} style={{ textAlign: 'center', padding: 24, color: 'var(--joy-palette-neutral-500)' }}>No tokens yet</td></tr>
              )}
              {tokens.map(t => (
                <tr key={t.id}>
                  <td>{t.name}</td>
                  <td style={{ fontFamily: 'monospace', wordBreak: 'break-all', fontSize: 12 }}>{t.token}</td>
                  <td>{new Date(t.createdAt).toLocaleString()}</td>
                  <td>
                    <Stack direction="row" spacing={1}>
                      <Button size="sm" variant="soft" onClick={() => copy(t.token)}>Copy</Button>
                      <Button size="sm" variant="soft" color="danger" onClick={() => handleDelete(t.id)}>Delete</Button>
                    </Stack>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Sheet>
      </Stack>
    </Box>
  );
}
